// QUITAR UNA ETIQUETA EN MASA, de clientes o de pólizas.
//
// Lissette 2026-10-22 (ticket de German Pomares): "¿se puede quitar
// masivamente las etiquetas que se colocaron durante el año? Si un cliente era
// 'cliente nuevo' este año, el que viene ya no lo será". La IA solo sabía
// decir que se quitaban a mano, una por una.
//
// Dos maneras de abrirla:
//   - con `ids`  → solo de las fichas seleccionadas en la lista;
//   - sin `ids`  → de TODAS las que la llevan (lo decide el servidor, porque la
//                  lista del navegador puede no tener cargada toda la cartera).
// El trabajo lo hacen POST /clients/tags/remove y /policies/tags/remove.

import { useState } from 'react';
import { Tag } from 'lucide-react';
import { Modal } from './ui.jsx';
import { toast } from './Toast.jsx';
import { quitarEtiquetaDeClientes, quitarEtiquetaDePolizas } from '../api.js';

/** La lista de etiquetas sin `etiqueta` (sin distinguir mayúsculas). */
export function sinEtiqueta(tags, etiqueta) {
  const k = String(etiqueta || '').trim().toLowerCase();
  return (Array.isArray(tags) ? tags : []).filter((t) => String(t ?? '').trim().toLowerCase() !== k);
}

export function QuitarEtiquetaModal({ de = 'clientes', opciones = [], ids = null, inicial = '', lang = 'es', onClose, onDone }) {
  const es = lang === 'es';
  const [etiqueta, setEtiqueta] = useState(inicial || (opciones[0]?.label ?? ''));
  const [ocupado, setOcupado] = useState(false);
  const deClientes = de === 'clientes';
  const elegida = opciones.find((o) => o.label === etiqueta);
  const todos = !Array.isArray(ids) || ids.length === 0;

  const aplicar = async () => {
    if (!etiqueta || ocupado) return;
    setOcupado(true);
    try {
      const fn = deClientes ? quitarEtiquetaDeClientes : quitarEtiquetaDePolizas;
      const r = await fn(etiqueta, todos ? undefined : ids);
      const n = r.quitadas || 0;
      // Lo que CONTESTÓ el servidor, no lo que se pensó mandar (regla 142).
      toast.success(es
        ? `"${etiqueta}" quitada de ${n} ${deClientes ? 'cliente(s)' : 'póliza(s)'}`
        : `"${etiqueta}" removed from ${n} ${deClientes ? 'client(s)' : 'policy(ies)'}`);
      if (typeof onDone === 'function') onDone({ etiqueta, ids: r.ids || [] });
      onClose();
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo quitar la etiqueta' : 'Could not remove the tag'));
    } finally {
      setOcupado(false);
    }
  };

  const titulo = es ? 'Quitar una etiqueta' : 'Remove a tag';
  return (
    <Modal title={titulo} onClose={() => !ocupado && onClose()}>
      <div style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '.9rem', lineHeight: 1.5 }}>
        {todos
          ? (es
            ? `Se quita de TODOS los ${deClientes ? 'clientes' : 'pólizas'} que la tengan (también los que no están cargados en esta lista). La etiqueta sigue en la lista para volver a usarla.`
            : `It's removed from ALL ${deClientes ? 'clients' : 'policies'} that have it (including ones not loaded in this list). The tag stays in the list for reuse.`)
          : (es
            ? `Se quita solo de ${ids.length} ${deClientes ? 'cliente(s)' : 'póliza(s)'} seleccionado(s).`
            : `It's removed only from the ${ids.length} selected ${deClientes ? 'client(s)' : 'policy(ies)'}.`)}
      </div>

      {opciones.length === 0 ? (
        <div style={{ fontSize: '.82rem', color: 'var(--c-text3,#45688a)' }}>
          {es ? 'Lo seleccionado no tiene etiquetas.' : 'The selection has no tags.'}
        </div>
      ) : (
        <>
          <label className="dkt-input-lbl">{es ? 'Etiqueta' : 'Tag'}</label>
          <select className="dkt-inp" value={etiqueta} onChange={(e) => setEtiqueta(e.target.value)}>
            {opciones.map((o) => <option key={o.label} value={o.label}>{o.label}{o.n != null ? ` (${o.n})` : ''}</option>)}
          </select>
          {elegida && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginTop: '.4rem' }}>
              {es ? `En esta lista: ${elegida.n} ${deClientes ? 'cliente(s)' : 'póliza(s)'}` : `In this list: ${elegida.n}`}
            </div>
          )}
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem', marginTop: '1.1rem' }}>
        <button type="button" className="dkt-btn-g" onClick={onClose} disabled={ocupado}>{es ? 'Cancelar' : 'Cancel'}</button>
        <button type="button" className="dkt-btn-p" onClick={aplicar} disabled={!etiqueta || ocupado}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: !etiqueta || ocupado ? 0.5 : 1 }}>
          <Tag size={14} />
          {ocupado ? (es ? 'Quitando…' : 'Removing…') : (es ? 'Quitar etiqueta' : 'Remove tag')}
        </button>
      </div>
    </Modal>
  );
}
